import { useEffect, useMemo, useRef } from "react";
import * as echarts from "echarts";
import { useTranslation } from "react-i18next";

interface EChartsRegressionChartProps {
  x: number[];
  y: number[];
  predicted?: number[];
  upper?: number[];
  lower?: number[];
  height?: number | string;
  pointSize?: number;
  xAxisLabel?: string;
  yAxisLabel?: string;
  yAxisFormatter?: (val: number) => string;
}

const EChartsRegressionChart = ({
  x,
  y,
  predicted,
  upper,
  lower,
  height = 400,
  pointSize = 12,
  xAxisLabel = "X",
  yAxisLabel = "Y",
  yAxisFormatter,
}: EChartsRegressionChartProps) => {
  const { t } = useTranslation('dataCloud');
  const chartRef = useRef<HTMLDivElement>(null);
  const chartInstance = useRef<echarts.ECharts | null>(null);
  const pointsRef = useRef<[number, number][]>([]);

  // Function to reset chart to empty state
  const resetChart = () => {
    if (chartInstance.current) {
      chartInstance.current.setOption(
        {
          series: [],
          xAxis: {
            type: "value",
            name: "X",
            scale: true,
          },
          yAxis: {
            type: "value",
            name: "Y",
            scale: true,
          },
        },
        true
      );
    }
  };

  // Nova funkcija: filtrira sve serije zajedno tako da ostanu samo zajednički validni X i Y (i predicted, upper, lower ako postoje)
  function cleanAllSeries(
    x: number[],
    y: number[],
    predicted?: number[],
    upper?: number[],
    lower?: number[]
  ): {
    x: number[];
    y: number[];
    predicted?: number[];
    upper?: number[];
    lower?: number[];
  } {
    const resultX: number[] = [];
    const resultY: number[] = [];
    const resultPred: number[] = [];
    const resultUpper: number[] = [];
    const resultLower: number[] = [];

    // Allow duplicate X values - commenting out the Set
    // const seen = new Set<number>();

    for (let i = 0; i < x.length; i++) {
      const xi = x[i];
      const yi = y[i];
      const pi = predicted?.[i];
      const up = upper?.[i];
      const low = lower?.[i];

      // More permissive validity check - only check x and y
      const valid =
        xi !== undefined && yi !== undefined && !isNaN(xi) && !isNaN(yi);

      // No longer checking for duplicates, accepting all valid points
      if (valid) {
        resultX.push(xi);
        resultY.push(yi);
        if (predicted)
          resultPred.push(pi !== undefined && !isNaN(pi) ? pi : yi);
        if (upper) resultUpper.push(up !== undefined && !isNaN(up) ? up : yi);
        if (lower)
          resultLower.push(low !== undefined && !isNaN(low) ? low : yi);
      }
    }

    return {
      x: resultX,
      y: resultY,
      ...(predicted ? { predicted: resultPred } : {}),
      ...(upper ? { upper: resultUpper } : {}),
      ...(lower ? { lower: resultLower } : {}),
    };
  }

  // Derived chart data is recomputed ONLY when the input arrays actually
  // change identity. The parent memoizes these arrays, so on unrelated
  // re-renders (e.g. typing in another field) this whole block is skipped,
  // keeping large data clouds responsive.
  const { filtered, scatterInside, scatterOutside, lineUpper, lineLower, lineMiddle } =
    useMemo(() => {
      // Process all data points (no sampling)
      const filtered = cleanAllSeries(x, y, predicted, upper, lower);

      // Priprema scatter podataka
      const scatterInside: [number, number][] = [];
      const scatterOutside: [number, number][] = [];

      // Include all points in the "inside" category if no bounds or data is sparse
      const hasValidBounds =
        filtered.upper &&
        filtered.lower &&
        filtered.upper.length > 0 &&
        filtered.lower.length > 0;

      // Force inclusion of all points in "inside" if we have very few points (less than 10)
      const shouldForceAllInside = filtered.x.length < 10;

      for (let i = 0; i < filtered.x.length; i++) {
        const xi = filtered.x[i];
        const yi = filtered.y[i];
        const up = filtered.upper?.[i];
        const low = filtered.lower?.[i];

        if (shouldForceAllInside) {
          // Force all points to be inside if we have very few points
          scatterInside.push([xi, yi]);
        } else if (hasValidBounds && up !== undefined && low !== undefined) {
          if (yi <= up && yi >= low) {
            scatterInside.push([xi, yi]);
          } else {
            scatterOutside.push([xi, yi]);
          }
        } else {
          // No bounds or invalid bounds, put all pointss inside
          scatterInside.push([xi, yi]);
        }
      }

      // Priprema linija
      const lineUpper: [number, number][] = filtered.upper
        ? filtered.x.map((xi, i) => [xi, filtered.upper![i]])
        : [];
      const lineLower: [number, number][] = filtered.lower
        ? filtered.x.map((xi, i) => [xi, filtered.lower![i]])
        : [];
      const lineMiddle: [number, number][] =
        filtered.upper && filtered.lower
          ? filtered.x.map((xi, i) => [
              xi,
              (filtered.upper![i] + filtered.lower![i]) / 2,
            ])
          : [];

      return { filtered, scatterInside, scatterOutside, lineUpper, lineLower, lineMiddle };
    }, [x, y, predicted, upper, lower]);

  // Keep a ref of the current scatter points so the dataZoom handler (below)
  // can read the latest data without being re-registered on every data change.
  useEffect(() => {
    pointsRef.current = [...scatterInside, ...scatterOutside];
  }, [scatterInside, scatterOutside]);

  // 1. Init/dispose ECharts instance ONLY on mount/unmount
  useEffect(() => {
    if (!chartRef.current) return;
    chartInstance.current = echarts.init(chartRef.current);

    // Initialize with empty state
    resetChart();

    // Handle window resize
    const handleResize = () => {
      chartInstance.current?.resize();
    };
    window.addEventListener('resize', handleResize);

    // Initial resize after a short delay to ensure container has proper dimensions
    const resizeTimeout = setTimeout(() => {
      chartInstance.current?.resize();
    }, 100);

    // Auto-fit the y-axis to whatever points are visible in the current
    // x-range whenever the user zooms/pans (wheel, inside-drag, or the
    // toolbox box-zoom — all of these fire the 'dataZoom' event). The
    // yFitting guard prevents our own setOption below from re-triggering
    // this same handler.
    let yFitting = false;
    const chart = chartInstance.current;
    const onDataZoom = () => {
      if (!chart || yFitting) return;
      // Visible x-range = the plot area's left/right pixels converted to data.
      const w = chart.getWidth();
      const h = chart.getHeight();
      // Two points on the plotting grid; y pixel is arbitrary for an x conversion.
      const left = (chart.convertFromPixel({ xAxisIndex: 0 }, [0, h / 2]) as number[])[0];
      const right = (chart.convertFromPixel({ xAxisIndex: 0 }, [w, h / 2]) as number[])[0];
      const xMin = Math.min(left, right);
      const xMax = Math.max(left, right);
      let lo = Infinity, hi = -Infinity;
      for (const p of pointsRef.current) {
        if (p[0] < xMin || p[0] > xMax) continue;
        if (p[1] < lo) lo = p[1];
        if (p[1] > hi) hi = p[1];
      }
      if (lo === Infinity) return; // nothing visible → leave y as-is
      const pad = lo === hi ? (Math.abs(lo) * 0.05 || 1) : (hi - lo) * 0.05;
      yFitting = true;
      chart.setOption({ yAxis: { min: lo - pad, max: hi + pad } }, false);
      yFitting = false;
    };
    chart.on('dataZoom', onDataZoom);

    return () => {
      window.removeEventListener('resize', handleResize);
      clearTimeout(resizeTimeout);
      chart.off('dataZoom', onDataZoom);
      chartInstance.current?.dispose();
      chartInstance.current = null;
    };
  }, []);

  // 2. Update chart options on data change (no dispose/init)
  useEffect(() => {
    if (!chartInstance.current) return;

    // Check if we need to reset the chart (if input data is empty)
    if (!x || !y || x.length === 0 || y.length === 0) {
      // Clear the chart by setting empty series
      chartInstance.current.setOption(
        {
          series: [],
        },
        true
      );
      return;
    }

    // Before a regression runs there are no tolerance bounds, so every point
    // is "inside" — labelling them inside/outside is misleading. In that state
    // present a single neutral "data points" series instead.
    const hasBounds =
      (!!upper && upper.length > 0) || (!!lower && lower.length > 0);
    const insideName = hasBounds ? t('chart.insideBoundary') : t('chart.dataPoints');

    const option: echarts.EChartsOption = {
      // Entry animation is always off so the first paint is instant and
      // zoom/pan stays smooth regardless of how many points are loaded —
      // no point-count threshold.
      animation: false,
      tooltip: {
        trigger: "item",
        axisPointer: {
          type: "cross",
        },
        formatter: (params: unknown) => {
          const p = params as { value?: [number, number] };
          if (!Array.isArray(p.value)) return '';
          const fmt = yAxisFormatter ?? ((v: number) => String(v));
          return `${xAxisLabel}: ${fmt(p.value[0])}<br/>${yAxisLabel}: ${fmt(p.value[1])}`;
        },
      },
      // Zoom: mouse-wheel / pinch / drag inside the plot (both axes) plus a
      // toolbox box-zoom and a restore button to reset the view.
      dataZoom: [
        { type: "inside", xAxisIndex: 0, filterMode: "none" },
      ],
      toolbox: {
        right: 20,
        feature: {
          dataZoom: { yAxisIndex: "all", title: { zoom: t('chart.zoom'), back: t('chart.zoomReset') } },
          restore: { title: t('chart.zoomReset') },
        },
      },
      legend: {
        // Legend markers mirror the diagram (#118): a dashed line for the
        // boundary, a dotted line for the centre line, filled dots for the
        // scatter series — not the default hollow circles in palette colours.
        data: [
          ...((upper && upper.length > 0) || (lower && lower.length > 0)
            ? [{ name: t('chart.boundary'), icon: 'path://M0,6 h7 v2 h-7 Z M11,6 h7 v2 h-7 Z M22,6 h7 v2 h-7 Z' }]
            : []),
          ...(upper && lower
            ? [{ name: t('chart.centerLine'), icon: 'path://M1,6 h2.5 v2.5 h-2.5 Z M8,6 h2.5 v2.5 h-2.5 Z M15,6 h2.5 v2.5 h-2.5 Z M22,6 h2.5 v2.5 h-2.5 Z' }]
            : []),
          { name: insideName, icon: 'circle' },
          ...(hasBounds ? [{ name: t('chart.outsideBoundary'), icon: 'circle' }] : []),
        ],
      },
      xAxis: {
        type: "value",
        name: xAxisLabel,
        // No fixed min/max: dataZoom owns the visible window, otherwise the
        // axis extent would stay pinned to the full data range and zooming
        // would have no visible effect. `scale: true` keeps a tidy fit.
        scale: true,
        axisPointer: {
          snap: false,
        },
        nameLocation: "middle",
        nameGap: 30,
        nameTextStyle: {
          fontSize: 14,
          fontWeight: "bold",
        },
        splitLine: {
          show: true,
          lineStyle: {
            type: "dashed",
            opacity: 0.5,
          },
        },
      },
      yAxis: {
        type: "value",
        name: yAxisLabel,
        scale: true,
        nameLocation: "middle",
        nameGap: 50,
        nameRotate: 90,
        nameTextStyle: {
          fontSize: 14,
          fontWeight: "bold",
        },
        axisLabel: {
          formatter: yAxisFormatter
            ? yAxisFormatter
            : (val: number) => String(val),
          rotate: 0,
        },
        splitLine: {
          show: true,
          lineStyle: {
            type: "dashed",
            opacity: 0.5,
          },
        },
      },
      series: (
        [
          predicted && predicted.length > 0
            ? {
                name: t('chart.boundary'),
                type: "line" as const,
                step: false,
                data: lineUpper,
                lineStyle: { color: "#004225", type: "dashed" },
                itemStyle: { color: "#004225" },
                showSymbol: false,
                z: 2,
              }
            : undefined,
          lower && lower.length > 0
            ? {
                name: t('chart.boundary'),
                type: "line" as const,
                step: false,
                data: lineLower,
                lineStyle: { color: "#004225", type: "dashed" },
                itemStyle: { color: "#004225" },
                showSymbol: false,
                z: 2,
              }
            : undefined,
          upper && lower
            ? {
                name: t('chart.centerLine'),
                type: "line" as const,
                step: false,
                data: lineMiddle,
                lineStyle: { color: "#666666", type: "dotted", width: 2.5 },
                itemStyle: { color: "#666666" },
                showSymbol: false,
                z: 2,
              }
            : undefined,
          {
            name: insideName,
            type: "scatter" as const,
            step: false,
            data: scatterInside,
            itemStyle: { color: "#0D9488" },
            symbolSize: pointSize,
            z: 4,
            symbol: "circle",
            emphasis: {
              itemStyle: {
                shadowBlur: 10,
                shadowColor: "rgba(0, 0, 0, 0.3)",
              },
            },
            large: filtered.x.length > 100,
            largeThreshold: 100,
          },
          hasBounds
            ? {
                name: t('chart.outsideBoundary'),
                type: "scatter" as const,
                step: false,
                data: scatterOutside,
                itemStyle: { color: "#dc2626" },
                symbolSize: pointSize,
                z: 4,
                symbol: "circle",
                emphasis: {
                  itemStyle: {
                    shadowBlur: 10,
                    shadowColor: "rgba(0, 0, 0, 0.3)",
                  },
                },
                large: scatterOutside.length > 100,
                largeThreshold: 100,
              }
            : undefined,
        ] as echarts.SeriesOption[]
      ).filter((s): s is echarts.SeriesOption => s !== undefined),
      grid: {
        left: 90, // Povećana vrednost da ima više prostora za y-osu i labelu
        right: 40, // Povećan prostor na desnoj strani
        top: 60, // Povećan prostor na vrhu za naslov
        bottom: 60, // Dovoljno prostora na dnu za x-osu
        containLabel: true, // Važno - osigurava da su labele uključene u oblast grafikona
      },
    };
    chartInstance.current.setOption(option, true);
    // Derived series come from the memo above, which is keyed on the same
    // inputs, so this list stays correct while avoiding redundant re-runs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtered, scatterInside, scatterOutside, lineUpper, lineLower, lineMiddle, pointSize]);

  return (
    <div
      className="relative"
      role="img"
      aria-label={t('chart.a11ySummary', {
        count: x?.length ?? 0,
        x: xAxisLabel,
        y: yAxisLabel,
      })}
    >
      <div
        ref={chartRef}
        style={{
          width: "100%",
          height,
          maxWidth: "100%",
          margin: "0 auto",
        }}
      />
    </div>
  );
};

export default EChartsRegressionChart;
